import React, { useState } from 'react';
import './Careers.css';

const DEPARTMENTS = ['All', 'Engineering', 'Design', 'Marketing', 'Operations'];

const JOBS = [
  {
    id: 1,
    title: 'Full-Stack Engineer',
    department: 'Engineering',
    type: 'Full-time',
    location: 'Remote (US)',
    description: 'Build and ship core product features, from the auto-apply engine to the recruiter outreach pipeline. You own features end-to-end.',
    responsibilities: [
      'Design and implement new product features across the React frontend and Python backend',
      'Build and maintain the browser automation pipeline that submits job applications',
      'Optimize cold email delivery and personalization systems',
      'Write clean, tested code and participate in code reviews',
    ],
    qualifications: [
      '2+ years of experience with React and Node.js or Python',
      'Comfortable with browser automation tools (Puppeteer, Playwright, or similar)',
      'Strong understanding of REST APIs and database design',
      'Bonus: experience with LLMs or AI-powered features',
    ],
  },
  {
    id: 2,
    title: 'Backend Engineer',
    department: 'Engineering',
    type: 'Full-time',
    location: 'Remote (US)',
    description: 'Own the infrastructure that powers thousands of automated applications daily. Scale our systems from hundreds to millions of jobs processed.',
    responsibilities: [
      'Build scalable job scraping and data ingestion pipelines',
      'Design and optimize the application submission queue system',
      'Implement monitoring, alerting, and reliability improvements',
      'Work with the AI team to deploy and serve LLM-powered features',
    ],
    qualifications: [
      '3+ years backend experience with Python, Go, or similar',
      'Experience with distributed systems, queues (Redis, RabbitMQ), and databases (PostgreSQL, MongoDB)',
      'Familiarity with cloud infrastructure (AWS or GCP)',
      'Strong debugging and performance optimization skills',
    ],
  },
  {
    id: 3,
    title: 'ML / AI Engineer',
    department: 'Engineering',
    type: 'Full-time',
    location: 'Remote (US)',
    description: 'Make our cold emails feel human and our job matching feel magical. Build the AI layer that makes ApplyAI smarter with every application.',
    responsibilities: [
      'Fine-tune and prompt-engineer LLMs for personalized cold email generation',
      'Build job-to-candidate matching and ranking models',
      'Develop resume parsing and skill extraction pipelines',
      'Run experiments to measure and improve application success rates',
    ],
    qualifications: [
      '2+ years working with LLMs, NLP, or ML in production',
      'Experience with OpenAI API, LangChain, or similar frameworks',
      'Strong Python skills and comfort with data pipelines',
      'Bonus: experience in recruiting tech or HR tech',
    ],
  },
  {
    id: 4,
    title: 'Product Designer',
    department: 'Design',
    type: 'Full-time',
    location: 'Remote (US)',
    description: 'Design the experience that turns a stressful job search into a 5-minute setup. Own the end-to-end design from research to shipped pixels.',
    responsibilities: [
      'Design user flows for onboarding, dashboard, and application tracking',
      'Create high-fidelity mockups and interactive prototypes in Figma',
      'Conduct user research and usability testing with students',
      'Build and maintain our design system and component library',
    ],
    qualifications: [
      '2+ years product design experience, preferably in SaaS or consumer products',
      'Strong Figma skills with a portfolio showing end-to-end product work',
      'Understanding of frontend implementation (HTML/CSS/React basics)',
      'Empathy for early-career job seekers, bonus if you\'ve been one recently',
    ],
  },
  {
    id: 5,
    title: 'Growth Marketing Manager',
    department: 'Marketing',
    type: 'Full-time',
    location: 'Remote (US)',
    description: 'Drive user acquisition across every channel that matters for college students. Build the growth engine from SEO to TikTok.',
    responsibilities: [
      'Plan and execute campaigns across social media, SEO, and paid channels',
      'Build referral and viral growth loops targeting university communities',
      'Manage content calendar and collaborate with creators and campus ambassadors',
      'Track and report on acquisition metrics, CAC, and conversion funnels',
    ],
    qualifications: [
      '2+ years in growth or digital marketing, ideally targeting Gen Z or students',
      'Experience with social media marketing (TikTok, Instagram, LinkedIn)',
      'Data-driven mindset, comfortable with analytics tools and A/B testing',
      'Strong copywriting skills and a feel for what resonates with college students',
    ],
  },
  {
    id: 6,
    title: 'Content Marketing Intern',
    department: 'Marketing',
    type: 'Internship',
    location: 'Remote',
    description: 'Write the content that helps students land jobs: blog posts, email templates, career guides. You\'ll shape our brand voice.',
    responsibilities: [
      'Write 2-3 blog posts per week on job search tips, cold emailing, and interview prep',
      'Create social media content and short-form video scripts',
      'Help build our email newsletter and drip campaigns',
      'Research trending topics in the internship and early-career space',
    ],
    qualifications: [
      'Currently enrolled in college or recent graduate',
      'Strong writing skills, send us your best blog post or essay',
      'Active on social media with an understanding of what content performs',
      'Bonus: personal experience with the pain of job applications',
    ],
  },
  {
    id: 7,
    title: 'Founding Operations Lead',
    department: 'Operations',
    type: 'Full-time',
    location: 'Remote (US)',
    description: 'Be the person who makes everything work. From customer support to partnerships to keeping the team running. You\'re the operational backbone.',
    responsibilities: [
      'Manage customer support and build our help center documentation',
      'Coordinate with university career centers for partnerships and distribution',
      'Handle vendor relationships, billing, and compliance requirements',
      'Build internal processes and tools as the team scales from 5 to 15',
    ],
    qualifications: [
      '2+ years in operations, business development, or a chief-of-staff role',
      'Exceptional organizational skills and ability to context-switch constantly',
      'Strong written communication, you\'ll write a lot of emails',
      'Startup experience or comfort with ambiguity and wearing many hats',
    ],
  },
];

const JobCard = ({ job, isExpanded, onToggle }) => (
  <div className={`careers-card${isExpanded ? ' careers-card--expanded' : ''}`}>
    <div className="careers-card-header" onClick={onToggle}>
      <div className="careers-card-info">
        <h3 className="careers-card-title">{job.title}</h3>
        <div className="careers-card-meta">
          <span className="careers-pill careers-pill--dept">{job.department}</span>
          <span className="careers-pill careers-pill--type">{job.type}</span>
          <span className="careers-card-location">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
            {job.location}
          </span>
        </div>
      </div>
      <span className="careers-card-toggle">{isExpanded ? '−' : '+'}</span>
    </div>
    {isExpanded && (
      <div className="careers-card-body">
        <p className="careers-card-desc">{job.description}</p>
        <div className="careers-card-section">
          <h4>What you'll do</h4>
          <ul>
            {job.responsibilities.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        </div>
        <div className="careers-card-section">
          <h4>What we're looking for</h4>
          <ul>
            {job.qualifications.map((q, i) => <li key={i}>{q}</li>)}
          </ul>
        </div>
        <a href={`mailto:careers@applyai.com?subject=Application: ${job.title}`} className="careers-apply-btn">
          Apply for this role &rarr;
        </a>
      </div>
    )}
  </div>
);

const Careers = () => {
  const [filter, setFilter] = useState('All');
  const [expandedId, setExpandedId] = useState(null);

  const filtered = filter === 'All' ? JOBS : JOBS.filter(j => j.department === filter);

  return (
    <section className="careers-section">
      <div className="careers-inner">
        {/* Header */}
        <div className="careers-header">
          <span className="careers-kicker">CAREERS</span>
          <h1 className="careers-heading">Build the future of job search</h1>
          <p className="careers-subtitle">
            We're a small team solving a massive problem. If you're excited about automating the worst part of career building, we want to hear from you.
          </p>
        </div>

        {/* Values */}
        <div className="careers-values">
          <div className="careers-value">
            <div className="careers-value-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
            </div>
            <h3>Ship fast</h3>
            <p>We deploy daily. Ideas go from whiteboard to production in days, not quarters.</p>
          </div>
          <div className="careers-value">
            <div className="careers-value-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
            </div>
            <h3>Small team, big impact</h3>
            <p>Every person shapes the product. No layers of approval, your work goes directly to users.</p>
          </div>
          <div className="careers-value">
            <div className="careers-value-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>
            </div>
            <h3>Remote-first</h3>
            <p>Work from anywhere in the US. We care about output, not office hours.</p>
          </div>
        </div>

        {/* Open positions */}
        <div className="careers-positions">
          <div className="careers-positions-header">
            <h2 className="careers-positions-title">
              Open positions
              <span className="careers-count">{JOBS.length}</span>
            </h2>
            <div className="careers-filters">
              {DEPARTMENTS.map(d => (
                <button
                  key={d}
                  className={`careers-filter-btn${filter === d ? ' careers-filter-btn--active' : ''}`}
                  onClick={() => { setFilter(d); setExpandedId(null); }}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>

          <div className="careers-list">
            {filtered.map(job => (
              <JobCard
                key={job.id}
                job={job}
                isExpanded={expandedId === job.id}
                onToggle={() => setExpandedId(expandedId === job.id ? null : job.id)}
              />
            ))}
          </div>

          {filtered.length === 0 && (
            <p className="careers-empty">No open positions in this department right now. Check back soon!</p>
          )}
        </div>

        {/* Bottom CTA */}
        <div className="careers-cta">
          <h3>Don't see your role?</h3>
          <p>We're always looking for exceptional people. Send us a note and tell us what you'd bring to ApplyAI.</p>
          <a href="mailto:careers@applyai.com?subject=General Application" className="careers-cta-btn">
            Send an open application &rarr;
          </a>
        </div>
      </div>
    </section>
  );
};

export default Careers;
